import { portalText } from "../../../i18n/portal.js";
/**
 * What a `text` field of an agent_template expects to be written as — customer-portal review #5
 * (fix round D, 2026-09-25).
 *
 * The content step used to draw every `text` field as a plain box, so "1,50 €", "none" and a
 * free-text week all passed and the build failed afterwards, leaving a dead agent. The factory reads
 * three of the delivery fields by a grammar (`apps/factory/src/build/draft-input.ts`): the fee as an
 * amount, the free-delivery threshold as an amount or "none", the opening hours as day-and-time
 * clauses. The wizard now checks the first two with the factory's own rule and draws the third as a
 * structured editor that can only produce text the factory reads (`./opening-hours.ts`).
 *
 * F-FAC-1 STILL HOLDS: "adding a template must not change wizard code". This is a register of
 * FORMATS by field code, not a branch per template — a field it does not list is a plain required
 * text box, exactly as before, so a new template renders without touching it.
 */
import type { AgentTemplateField } from "@wetalk/domain";

export type FieldFormat = "amount" | "amount_or_none" | "opening_hours";

const FIELD_FORMAT: Readonly<Record<string, FieldFormat>> = {
  delivery_fee: "amount",
  free_delivery_over: "amount_or_none",
  opening_hours: "opening_hours",
};

/** The example a box shows while it is empty. */
const FORMAT_PLACEHOLDER: Readonly<Record<Exclude<FieldFormat, "opening_hours">, string>> = {
  get amount() { return portalText("operations", "2_50_0_if_delivery_is_free_0b723adc"); },
  get amount_or_none() { return portalText("operations", "25_00_or_none_87460a95"); },
};

/** The line under a box that says what it is for and how to write it. */
const FORMAT_DESCRIPTION: Readonly<Record<Exclude<FieldFormat, "opening_hours">, string>> = {
  get amount() { return portalText("operations", "in_euros_the_agent_adds_it_to_the_total_it_tells_the_calle_cd3cb6f2"); },
  get amount_or_none() { return portalText("operations", "in_euros_orders_over_this_amount_are_delivered_free_write_29b36172"); },
};

export function fieldFormatOf(field: AgentTemplateField): FieldFormat | null {
  if (field.kind !== "text") return null;
  const format = FIELD_FORMAT[field.code];
  return format === undefined ? null : format;
}

export function placeholderOf(format: Exclude<FieldFormat, "opening_hours">): string {
  return FORMAT_PLACEHOLDER[format];
}

export function descriptionOf(format: Exclude<FieldFormat, "opening_hours">): string {
  return FORMAT_DESCRIPTION[format];
}

// ── amounts: the factory's rule (`amountMinorOf`), restated so the wizard refuses what it would ──

const NO_AMOUNT: ReadonlySet<string> = new Set(["none", "no", "never", "-", "κανενα", "οχι"]);
const FREE: ReadonlySet<string> = new Set(["free", "δωρεαν"]);
const AMOUNT = /^(?:€|eur)?\s*(\d{1,6})(?:[.,](\d{1,2}))?\s*(?:€|eur|euro|euros|ευρω)?$/;

function normalised(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Why `text` is not an amount the build reads, in words — or `null` when it is. Empty is its own
 * problem ("required"), reported by the caller.
 */
export function amountProblem(text: string, format: "amount" | "amount_or_none"): string | null {
  const plain = normalised(text);
  if (NO_AMOUNT.has(plain)) {
    return format === "amount_or_none" ? null : portalText("operations", "write_the_fee_as_an_amount_for_example_2_50_or_0_if_delive_975d238a");
  }
  if (FREE.has(plain)) {
    return format === "amount" ? null : portalText("operations", "write_the_amount_orders_must_reach_for_example_25_00_or_no_fcaf87b6");
  }
  if (AMOUNT.test(plain)) return null;
  return format === "amount"
    ? portalText("operations", "value_is_not_an_amount_in_euros_write_it_as_2_50_or_0_if_d_fd4fb740", { v0: String(text.trim()) })
    : portalText("operations", "value_is_not_an_amount_in_euros_write_it_as_25_00_or_none_e237a3d9", { v0: String(text.trim()) });
}
